import React, { useState, useEffect, useCallback, useRef } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faPaperPlane, faSpinner, faRotate } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import RichTextEditor from './RichTextEditor';
import RichText from './RichText';
import { useRecentPicks } from '../utils/recentPicks';
import { RecentPickLabel, RecentPickDivider } from './RecentPickLabel';
import '../styles/whatsapp.css';

/**
 * WhatsApp announcements to selected employees, on the admin WhatsApp page.
 *
 * The server sends in the background, one message at a time, so the button
 * only queues it. The history polls while a send is in progress and lists who
 * the message did not reach and why.
 */

const STATUS = {
    sending: ['warn', 'Sending'],
    completed: ['ok', 'Completed'],
    interrupted: ['bad', 'Interrupted']
};

const departmentLabel = (department) => (!department || /not assigned/i.test(department) ? '' : department);

const WhatsAppAnnouncements = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [resending, setResending] = useState('');
    const [selected, setSelected] = useState(() => new Set());
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await api.get('/whatsapp/announcements');
            setData(res.data);
            setError('');
            // Drop anyone who has since left or lost their number.
            const reachable = new Set(res.data.audience.filter(p => p.reachable).map(p => String(p._id)));
            setSelected(prev => new Set([...prev].filter(id => reachable.has(id))));
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load announcements.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const inProgress = Boolean(data?.history?.some(item => item.status === 'sending'));
    useEffect(() => {
        if (!inProgress) return;
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
    }, [inProgress, load]);

    const maxLength = data?.maxLength || 2000;
    const text = message.trim();
    const people = data?.audience || [];
    const reachableCount = people.filter(p => p.reachable).length;
    const unreachableCount = people.length - reachableCount;

    const query = search.trim().toLowerCase();
    const visible = query
        ? people.filter(p => [p.name, p.employeeId, departmentLabel(p.department)].some(v => String(v || '').toLowerCase().includes(query)))
        : people;
    const visibleReachable = visible.filter(p => p.reachable);
    const allVisibleSelected = visibleReachable.length > 0 && visibleReachable.every(p => selected.has(String(p._id)));

    const searchRef = useRef(null);
    const recent = useRecentPicks('whatsapp-recipient');
    // With nothing typed, the people picked lately come first.
    const { recent: recentRows, rest: otherRows } = query ? { recent: [], rest: visible } : recent.split(visible);
    const listed = [...recentRows, ...otherRows];

    const toggle = (id) => {
        if (!selected.has(id)) recent.record(id);
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
        // Ready for the next name: the search empties and keeps the cursor.
        // "Select all shown" keeps the search, since it acts on what it found.
        setSearch('');
        searchRef.current?.focus();
    };

    const toggleAllVisible = () => setSelected(prev => {
        const next = new Set(prev);
        for (const p of visibleReachable) {
            if (allVisibleSelected) next.delete(String(p._id)); else next.add(String(p._id));
        }
        return next;
    });

    const handleSend = async () => {
        if (!text || !selected.size) return;
        const count = selected.size;
        const confirm = await Swal.fire({
            title: `Send to ${count} ${count === 1 ? 'person' : 'people'}?`,
            text: 'This WhatsApp message goes to every employee you selected. It cannot be recalled once sent.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#215D7B',
            confirmButtonText: 'Yes, send it'
        });
        if (!confirm.isConfirmed) return;

        setSending(true);
        try {
            await api.post('/whatsapp/announcements', { message: text, userIds: [...selected] });
            setMessage('');
            setSelected(new Set());
            setSearch('');
            Swal.fire({
                icon: 'success', title: 'Sending…', text: 'Messages go out one at a time. Progress shows below.',
                toast: true, position: 'top-end', timer: 3000, showConfirmButton: false
            });
        } catch (err) {
            Swal.fire('Not sent', err.response?.data?.message || 'Could not send the announcement.', 'error');
        } finally {
            setSending(false);
            load();
        }
    };

    const handleResend = async (item) => {
        const confirm = await Swal.fire({
            title: `Resend to ${item.resendable} people?`,
            text: 'Only people the message definitely did not reach. Anyone whose send timed out is left out, since it may already have arrived.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#215D7B',
            confirmButtonText: 'Yes, resend'
        });
        if (!confirm.isConfirmed) return;

        setResending(item._id);
        try {
            await api.post(`/whatsapp/announcements/${item._id}/resend`);
        } catch (err) {
            Swal.fire('Not resent', err.response?.data?.message || 'Could not resend the announcement.', 'error');
        } finally {
            setResending('');
            load();
        }
    };

    const blockedReason = !data ? 'Loading…'
        : !data.connected ? 'Connect WhatsApp first'
            : inProgress ? 'An announcement is still sending'
                : !selected.size ? 'Select at least one employee'
                    : !text ? 'Write a message first' : '';

    return (
        <div className="control-card wa-card">
            <div className="wa-head">
                <h3 className="wa-title">
                    <FontAwesomeIcon icon={faBullhorn} /> Announcements
                </h3>
                <button className="wa-refresh" onClick={load} title="Refresh">
                    <FontAwesomeIcon icon={faRotate} />
                </button>
            </div>

            {error && <p className="wa-hint wa-error" role="alert">{error}</p>}

            <label className="input-label wa-field-label" htmlFor="wa-announcement">Message</label>
            <RichTextEditor
                id="wa-announcement"
                rows={5}
                placeholder="Write the announcement…"
                value={message}
                maxLength={maxLength}
                onChange={setMessage}
                disabled={sending}
            />

            <label className="input-label wa-field-label wa-recipients-label" htmlFor="wa-recipient-search">Recipients</label>
            <div className="wa-picker">
                <div className="wa-picker-bar">
                    <input
                        ref={searchRef}
                        id="wa-recipient-search"
                        type="search"
                        className="custom-input"
                        placeholder="Search by name, employee ID or department"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        disabled={sending}
                    />
                    <button
                        type="button"
                        className="gts-btn secondary"
                        onClick={toggleAllVisible}
                        disabled={sending || !visibleReachable.length}
                    >
                        {allVisibleSelected ? 'Deselect all' : 'Select all'}{query ? ' shown' : ''}
                    </button>
                    {selected.size > 0 && (
                        <button type="button" className="gts-btn secondary" onClick={() => setSelected(new Set())} disabled={sending}>
                            Clear
                        </button>
                    )}
                </div>
                <p className="wa-picker-summary" aria-live="polite">
                    <strong>{selected.size}</strong> of {reachableCount} selected
                    {unreachableCount > 0 && ` · ${unreachableCount} cannot be messaged`}
                </p>
                {!data ? (
                    <p className="wa-loading wa-picker-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading employees…</p>
                ) : !visible.length ? (
                    <p className="wa-hint wa-picker-empty">No employees match “{search}”.</p>
                ) : (
                    <ul className="wa-picker-list">
                        {listed.map((p, index) => {
                            const id = String(p._id);
                            const meta = [p.employeeId, departmentLabel(p.department)].filter(Boolean).join(' · ');
                            return (
                                <React.Fragment key={id}>
                                {index === 0 && recentRows.length > 0 && <li role="presentation"><RecentPickLabel /></li>}
                                {index === recentRows.length && index > 0 && <li role="presentation"><RecentPickDivider /></li>}
                                <li>
                                    <label className={`wa-picker-row ${p.reachable ? '' : 'is-disabled'}`}>
                                        <input
                                            type="checkbox"
                                            checked={selected.has(id)}
                                            disabled={!p.reachable || sending}
                                            onChange={() => toggle(id)}
                                        />
                                        <span className="wa-picker-name">{p.name}</span>
                                        {meta && <span className="wa-picker-meta">{meta}</span>}
                                        {!p.reachable && <span className="wa-picker-reason">{p.detail}</span>}
                                    </label>
                                </li>
                                </React.Fragment>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="wa-announce-foot">
                <p className="wa-hint wa-announce-audience">
                    {data && !data.connected
                        ? <span className="wa-error">WhatsApp is not connected. Start the session above before sending.</span>
                        : inProgress
                            ? 'An announcement is still sending. You can send another once it finishes.'
                            : 'Messages go out one at a time, a few seconds apart.'}
                </p>
                <button
                    className="gts-btn primary"
                    onClick={handleSend}
                    disabled={sending || Boolean(blockedReason)}
                    title={blockedReason || 'Send to the selected employees'}
                >
                    {sending
                        ? <><FontAwesomeIcon icon={faSpinner} spin /> Sending…</>
                        : <><FontAwesomeIcon icon={faPaperPlane} /> Send to {selected.size || 'selected'}</>}
                </button>
            </div>

            <section className="wa-section">
                <h4 className="wa-section-title">Recent announcements</h4>
                {!data ? (
                    <p className="wa-loading"><FontAwesomeIcon icon={faSpinner} spin /> Loading…</p>
                ) : !data.history.length ? (
                    <p className="wa-hint">No announcements sent yet.</p>
                ) : (
                    <ul className="wa-history">
                        {data.history.map(item => {
                            const [tone, label] = STATUS[item.status] || ['warn', item.status];
                            return (
                                <li key={item._id} className="wa-history-item">
                                    <RichText text={item.message} className="wa-history-message" />
                                    <div className="wa-history-meta">
                                        <span className={`wa-chip is-${tone}`}>{label}</span>
                                        <span>
                                            {item.sentBy || 'Former user'} · {new Date(item.createdAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                                        </span>
                                        <span>Sent {item.sent} of {item.total}</span>
                                        {item.failed > 0 && <span className="is-bad">Failed {item.failed}</span>}
                                        {item.skipped > 0 && <span>Not sent {item.skipped}</span>}
                                    </div>
                                    {item.undelivered.length > 0 && (
                                        <details className="wa-history-failures">
                                            <summary>Who it did not reach ({item.undelivered.length})</summary>
                                            <ul>
                                                {item.undelivered.map((r, i) => <li key={i}>{r.name} — {r.detail}</li>)}
                                            </ul>
                                        </details>
                                    )}
                                    {item.resendable > 0 && (
                                        <button
                                            className="gts-btn secondary wa-resend"
                                            onClick={() => handleResend(item)}
                                            disabled={Boolean(resending) || inProgress || !data.connected}
                                            title={!data.connected ? 'Connect WhatsApp first' : inProgress ? 'An announcement is still sending' : ''}
                                        >
                                            {resending === item._id
                                                ? <><FontAwesomeIcon icon={faSpinner} spin /> Resending…</>
                                                : <><FontAwesomeIcon icon={faPaperPlane} /> Resend to {item.resendable} who did not get it</>}
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
        </div>
    );
};

export default WhatsAppAnnouncements;
